import React, { useCallback, useEffect, useState } from 'react'
import { Checkbox, Collapse, Flex } from 'antd'
import CaretDownIcon from 'src/assets/icons/caret-down.svg?react'
import Select from '../../../components/common/select/Select'
import SelectMultiple from '../../../components/common/selectMultiple/SelectMultiple'
import SelectRange from '../../../components/common/selectRange/SelectRange'
import styles from './SidebarFilter.module.less'
import { FilteredTag } from 'src/hooks/useTag'
import { UIButton } from 'src/components/common/uiButton/UIButton'
import {
    FilteredValueType,
    FiltersKeyType,
    SkillLabelType,
    SkillType,
    TagType,
} from 'src/types/expert.types'
import { SkillValidationOptions, SkillValidationValue } from 'src/constants/experts.constants'
import { SkillFilterDataRespType } from 'src/types/api.types'
import { DataTestId, TextConstants } from 'src/constants/ui.constants'
import SegmentAnalytics from 'src/SegmentAnalytics'

const { SelectLevel, SelectValidationSource, SelectScore, ApplyFilters } =
    TextConstants.SidebarFilter

export type SidebarFilterProps = {
    id: string
    filterLabel: string
    levelOptions: { id: string; label: string; value: string }[]
    skillValidatedOptions: { label: string; value: string }[]
    updateTags: (newTag: FilteredTag) => void
    removedTagName: string
    filteredValues: { [key: string]: FilteredValueType }
    // eslint-disable-next-line
    setFilteredValues: (value: any) => void
    listOfSkills: SkillFilterDataRespType[]
    removeTags: (key: string, value: string) => void
}

type SingleSelectFilterType = string | string[] | [number, number] | undefined

const SidebarFilter: React.FC<SidebarFilterProps> = ({
    id,
    filterLabel,
    levelOptions,
    skillValidatedOptions,
    updateTags,
    removedTagName,
    filteredValues,
    setFilteredValues,
    listOfSkills,
    removeTags,
}) => {
    const [isFiltersApplied, setIsFiltersApplied] = useState<boolean>(false)
    const [selectedLevels, setSelectedLevels] = useState<string[]>([])
    const [selectedSkillValidated, setSelectedSkillValidated] = useState<string | undefined>(
        undefined
    )
    const [score, setScore] = useState<[number, number] | undefined>(undefined)
    const [isFilterCollapsed, setIsFilterCollapsed] = useState<boolean>(true)

    useEffect(() => {
        const currentFilters: FilteredValueType = filteredValues[id] || {}
        setSelectedLevels(currentFilters.level || [])
        setSelectedSkillValidated(currentFilters.skillIsValidatedBasedOn)
        setScore(currentFilters.score)
        setIsFiltersApplied(currentFilters.isFilterApplied || false)
    }, [filteredValues, id])

    const getFilterValue = useCallback(() => {
        return [
            selectedLevels.join(', '),
            SkillValidationOptions.find(option => option.value === selectedSkillValidated)?.label,
            score ? `(${score[0]}-${score[1]})` : '',
        ]
            .filter(Boolean)
            .join(' - ')
    }, [selectedLevels, selectedSkillValidated, score])

    const handleApplyFilter = useCallback(() => {
        setIsFiltersApplied(true)
        setIsFilterCollapsed(true)
        const filteredLevelIds = levelOptions
            .filter(option => selectedLevels.includes(option.label))
            .map(option => option.id)

        const value = getFilterValue()

        const newTag: FilteredTag = {
            key: filterLabel,
            id,
            filteredLevelIds,
            score: score ? score.join('/') : undefined,
            skillIsValidatedBasedOn: selectedSkillValidated,
            type: TagType.skill,
            value,
        }

        const newValues: { [key: string]: FilteredValueType } = {
            ...filteredValues,
            [id]: {
                level: selectedLevels,
                skillIsValidatedBasedOn: selectedSkillValidated,
                score: score,
                isFilterApplied: true,
            },
        }

        setFilteredValues(newValues)
        updateTags(newTag)
    }, [
        levelOptions,
        selectedLevels,
        selectedSkillValidated,
        score,
        filterLabel,
        id,
        updateTags,
        filteredValues,
        setFilteredValues,
        getFilterValue,
    ])

    useEffect(() => {
        if (removedTagName) {
            setIsFilterCollapsed(true)

            if (removedTagName === FiltersKeyType.SelectAll) {
                setFilteredValues({})
                return
            }

            const skillId = listOfSkills.find(skill => skill.filterLabel === removedTagName)?.id
            if (skillId) {
                setFilteredValues((prevValues: { [key: string]: FilteredValueType }) => {
                    return {
                        ...prevValues,
                        [skillId]: {
                            level: [],
                            skillIsValidatedBasedOn: undefined,
                            score: undefined,
                            isFilterApplied: false,
                        },
                    }
                })
            }
        }
    }, [removedTagName, listOfSkills, setFilteredValues])

    const isFormValid = useCallback(() => {
        return (
            isFiltersApplied ||
            selectedLevels.length > 0 ||
            selectedSkillValidated ||
            (score && score.length > 0)
        )
    }, [selectedLevels, selectedSkillValidated, score, isFiltersApplied])

    const shouldScoreFieldVisible = useCallback((): boolean => {
        const skillsToCheck = [
            SkillValidationValue.ProjectPerformance,
            SkillValidationValue.FTSExam,
            SkillValidationValue.QualificationTasks,
        ]
        return skillsToCheck.some(skill => selectedSkillValidated?.includes(skill))
    }, [selectedSkillValidated])

    const handleKeyDown = useCallback((e: { key: string }) => {
        if (e.key === 'Enter' || e.key === ' ') {
            setIsFilterCollapsed(prev => !prev)
        }
    }, [])

    const handleCheckbox = useCallback(() => {
        SegmentAnalytics.sendTrackEvent('FilterBySkill_Checkbox_Unchecked', {
            skillId: id,
            skillLabel: filterLabel,
        })

        removeTags(filterLabel, getFilterValue())
    }, [removeTags, id, filterLabel, getFilterValue])

    const handleOnDropdownVisibleChange = useCallback(
        (filterKey: string, dropdownVisibilityStatus: boolean) => {
            if (filterKey === SkillType.Level && !dropdownVisibilityStatus) {
                SegmentAnalytics.sendTrackEvent(
                    'FilterBySkill_Select_Level_dropdown',
                    {},
                    filterKey
                )
            }
        },
        []
    )

    const handleSelect = useCallback(
        (val: SingleSelectFilterType, skillType: SkillType) => {
            let label: string = ''
            if (skillType === SkillType.Score) {
                setScore(val as [number, number])
                label = SkillLabelType.Score
            } else if (skillType === SkillType.ValidatonSource) {
                setSelectedSkillValidated(val as string)
                label = SkillLabelType.ValidatonSource
            }

            SegmentAnalytics.sendTrackEvent(`FilterBySkill_Select_${label}_dropdown`, {
                skillId: id,
                skillLabel: filterLabel,
                value: val,
            })
        },
        [id, filterLabel]
    )

    const handleMultiSelect = useCallback(
        (val: string[]) => {
            SegmentAnalytics.appendEventData({
                skillId: id,
                skillLabel: filterLabel,
                value: val,
            })
            setSelectedLevels(val)
        },
        [id, filterLabel]
    )

    const handleCheckboxClickEvent = useCallback(() => {
        SegmentAnalytics.sendTrackEvent('FilterBySkill_Checkbox_Checked', {
            skillId: id,
            skillLabel: filterLabel,
        })
    }, [id, filterLabel])

    const handleApplyFilterEvent = useCallback(() => {
        SegmentAnalytics.sendTrackEvent('FilterBySkill_ApplyFiltersCTA_Clicked', {
            skillId: id,
            skillLabel: filterLabel,
            selectedLevels,
            score,
            selectedSkillValidated,
        })
    }, [id, filterLabel, selectedLevels, score, selectedSkillValidated])

    return (
        <div className={styles.sidebarFilter} data-testid={DataTestId.SidebarFilter}>
            <Collapse
                expandIcon={({ isActive }) => (
                    <CaretDownIcon
                        onClick={() => setIsFilterCollapsed(prev => !prev)}
                        style={{ transform: `rotate(${isActive ? 180 : 0}deg)` }}
                        data-testid={DataTestId.CaretDownIcon}
                    />
                )}
                ghost
                expandIconPosition="end"
                className={styles.collapse}
                activeKey={isFilterCollapsed ? [] : ['1']}
            >
                <Collapse.Panel
                    header={
                        <Flex align="center" gap="15px">
                            <Checkbox
                                checked={isFiltersApplied}
                                className={styles.checkbox}
                                onChange={e => {
                                    e.preventDefault()
                                    setIsFiltersApplied(e.target.checked)
                                    if (e.target.checked) {
                                        handleApplyFilter()
                                        handleCheckboxClickEvent()
                                    } else {
                                        handleCheckbox()
                                    }
                                }}
                                data-testid={DataTestId.SidebarFilterCheckbox}
                            />
                            <span
                                aria-expanded="false"
                                onClick={() => setIsFilterCollapsed(prev => !prev)}
                                onKeyDown={handleKeyDown}
                                tabIndex={0}
                                role="button"
                            >
                                {filterLabel}
                            </span>
                        </Flex>
                    }
                    key="1"
                >
                    <Flex className={styles.dropdowns} gap={'12px'} vertical>
                        <SelectMultiple
                            options={levelOptions}
                            placeholder={SelectLevel}
                            maxTextDisplayLength={18}
                            onChange={handleMultiSelect}
                            onDropdownVisibleChange={visible =>
                                handleOnDropdownVisibleChange(SkillType.Level, visible)
                            }
                            value={selectedLevels}
                        />
                        <Select
                            options={skillValidatedOptions}
                            placeholder={SelectValidationSource}
                            onChange={e => handleSelect(e, SkillType.ValidatonSource)}
                            value={selectedSkillValidated}
                            allowClear
                        />
                        {shouldScoreFieldVisible() && (
                            <SelectRange
                                step={0.1}
                                max={10}
                                min={0}
                                placeholder={SelectScore}
                                onChange={e => handleSelect(e, SkillType.Score)}
                                value={score}
                                allowClear
                                precision={1}
                                minLimit={7.2}
                                maxLimit={10.0}
                            />
                        )}
                        <UIButton
                            onClick={() => {
                                handleApplyFilter()
                                handleApplyFilterEvent()
                            }}
                            isDisabled={!isFormValid()}
                            title={ApplyFilters}
                            htmlType="submit"
                        />
                    </Flex>
                </Collapse.Panel>
            </Collapse>
        </div>
    )
}

export default SidebarFilter
